import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CopyIcon } from '@elementary/common-ui/assets/copy.svg';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import {
  AnomalyChart,
  AutoFreshnessChart,
  CodeEditor,
  Expandable,
  Flex,
  FlexCenter,
  FlexColumn,
  FreshnessChart,
  WidgetButton,
} from '@elementary/common-ui/components';
import SchemaChangesList from '@elementary/common-ui/components/SchemaChangesList';
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExceptionCard from 'shared/components/ExceptionCard';
import SamplesTable from 'shared/components/SamplesTable';
import useSnackbar from 'shared/hooks/useSnackbar';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import {
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_TEST_CONFIGURATION,
} from '@elementary/common-ui/constants/urlParams';
import {
  convertTableToCSV,
  downloadCSV,
  extractHeadersFromTableData,
} from 'shared/utils/csvExport';
import { SourceType } from 'shared/types';
import useTestOverviewExecution from 'shared/hooks/useTestOverviewExecution';
import {
  AnomalyChartTestExecutionData,
  AutoFreshnessTestExecutionData,
  CloudDataContractTestExecutionData,
  FreshnessChartExecutionData,
  SchemaChangesTestExecutionData,
  TableTestExecutionData,
  TestOverviewExecution,
} from 'shared/types/execution';
import ExecutionOverview from 'shared/components/ExecutionOverview';
import PanelError from 'shared/components/PanelError';
import CloudDataContractList from 'shared/components/CloudDataContractList';
import { fontWeight, radius, space } from '@elementary/common-ui/style/utils';
import ResultFeedbackDialog, {
  FeedbackEntryPoint,
  FeedbackTest,
  RemoveChangepointDialog,
  useChangepointMarkers,
} from 'shared/components/ResultFeedback';
import { removeBlankLines } from './utils';

const getTableData = (data: TestOverviewExecution | undefined) =>
  data?.data as TableTestExecutionData;

const getSchemaChangesData = (data: TestOverviewExecution | undefined) =>
  data?.data as SchemaChangesTestExecutionData;

const getAnomalyChartData = (data: TestOverviewExecution | undefined) =>
  data?.data as AnomalyChartTestExecutionData;

const getFreshnessChartData = (data: TestOverviewExecution | undefined) =>
  data?.data as FreshnessChartExecutionData;

const getAutoFreshnessData = (data: TestOverviewExecution | undefined) =>
  data?.data as AutoFreshnessTestExecutionData;

const getCloudDataContractData = (data: TestOverviewExecution | undefined) =>
  data?.data as CloudDataContractTestExecutionData;

const Description = styled(Typography)`
  font-weight: ${fontWeight('regular')};
  color: ${({ theme }) => theme.palette.content.primary};
  padding: ${space(0, 'sm')};
  line-height: normal;
  white-space: pre-line;
`;

const WidgetButtonStyled = styled(WidgetButton)`
  background: ${({ theme }) => theme.palette.surface.strong};
  color: ${({ theme }) => theme.palette.content.primary};
  border: 1px solid ${({ theme }) => theme.palette.border.muted};
  min-width: 0;
  height: 24px;
  border-radius: ${radius('lg')};
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;

  :hover {
    border: 1px solid ${({ theme }) => theme.palette.border.muted};
    background: ${({ theme }) => theme.palette.surface.control};
  }

  :disabled {
    background: ${({ theme }) => theme.palette.surface.hover};
    border: 1px solid ${({ theme }) => theme.palette.border.muted};
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: ${space('sm')};
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ActionIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

type Props = {
  id: string;
  failures?: number | null;
  description?: string | null;
  feedbackTest?: FeedbackTest;
  isCloudTest?: boolean;
  sourceType: SourceType;
};

const TestOverviewExecutionDetails = ({
  id,
  failures,
  description,
  feedbackTest,
  isCloudTest,
  sourceType,
}: Props) => {
  const { openSnackbar } = useSnackbar();
  const [, setSearchParams] = useSearchParams();
  const warehouseType = useSelectedWarehouseType();

  const canEditTestConfig = useEnvPermission()('EDIT_TEST_CONFIGURATIONS');
  const [feedbackDialog, setFeedbackDialog] = useState<{
    entryPoint: FeedbackEntryPoint;
    bucketStart?: string;
  }>();

  const { data, error, isFetching } =
    useTestOverviewExecution<TestOverviewExecution>(id, sourceType);

  const feedbackResult = useMemo(() => {
    if (data?.data?.type !== 'anomaly_chart') return undefined;
    return {
      id: data.id,
      status: data.status,
      chart: getAnomalyChartData(data),
    };
  }, [data]);

  const availableFeedbackTypes = data?.availableFeedbackTypes ?? [];

  const canGiveFeedback =
    !!feedbackTest &&
    !!feedbackResult &&
    feedbackResult.chart.metrics.length > 0 &&
    availableFeedbackTypes.length > 0 &&
    canEditTestConfig;

  const changepoints = useChangepointMarkers({
    test: feedbackTest,
    resultId: data?.id ?? '',
    metrics: feedbackResult?.chart.metrics,
    canEdit: canEditTestConfig,
    canGiveFeedback,
  });

  if (isFetching || error) {
    return (
      <FlexCenter sx={{ height: '100%', padding: space(0, 'xxl') }}>
        {error ? (
          <PanelError
            error={error as Error}
            errorName={'ApiError'}
            page={'TestOverviewExecutionDetails'}
          />
        ) : (
          <CircularProgress size={32} />
        )}
      </FlexCenter>
    );
  }

  const type = data?.data?.type;

  const handleExcludeRange = () => {
    setSearchParams((prev) => {
      prev.set(
        TEST_OVERVIEW_SIDEPANEL_TAB,
        TEST_OVERVIEW_TAB_TEST_CONFIGURATION,
      );
      return prev;
    });
  };

  const handleCopyTableAsCSV = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (type !== 'table') return;

    const tableData = getTableData(data);
    const tableRows = tableData?.data;

    if (!tableRows || tableRows.length === 0) {
      openSnackbar({ message: 'No data available to copy', variant: 'error' });
      return;
    }

    const headers = extractHeadersFromTableData(tableData, tableRows);

    const csvContent = convertTableToCSV(headers, tableRows);

    navigator.clipboard.writeText(csvContent);
    openSnackbar({
      message: 'Copied to clipboard',
      variant: 'success',
      duration: 1500,
    });
  };

  const handleDownloadTableAsCSV = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (type !== 'table') return;

    const tableData = getTableData(data);
    const tableRows = tableData?.data;

    if (!tableRows || tableRows.length === 0) {
      openSnackbar({
        message: 'No data available to download',
        variant: 'error',
      });
      return;
    }

    const headers = extractHeadersFromTableData(tableData, tableRows);

    const csvContent = convertTableToCSV(headers, tableRows);

    downloadCSV(csvContent, `result-samples-${id}.csv`);
  };

  const displayDescription = description ? removeBlankLines(description) : '';

  const shouldShowExpandable =
    !data?.exception && (displayDescription || data?.data);

  const trainingPeriod =
    type === 'auto_freshness'
      ? getAutoFreshnessData(data).trainingPeriod
      : type === 'anomaly_chart'
      ? getAnomalyChartData(data).trainingPeriod
      : null;

  const detectionPeriod =
    type === 'anomaly_chart' ? getAnomalyChartData(data).detectionPeriod : null;

  return (
    <>
      <ExecutionOverview
        data={data as TestOverviewExecution}
        trainingPeriod={trainingPeriod}
        detectionPeriod={detectionPeriod}
        onFeedbackClick={
          canGiveFeedback
            ? () => setFeedbackDialog({ entryPoint: 'result_button' })
            : undefined
        }
      />

      {data?.exception && <ExceptionCard exception={data?.exception} />}

      {shouldShowExpandable && (
        <Expandable
          title={
            <ActionButtonsContainer>
              <span>
                {failures
                  ? `Result sample${
                      type === 'table'
                        ? ` (${getTableData(data).data.length}/${failures})`
                        : ''
                    }`
                  : 'Result'}
              </span>
              {type === 'table' && (
                <Flex gap={1}>
                  <Tooltip title="Copy as CSV">
                    <ActionIconButton onClick={handleCopyTableAsCSV}>
                      <CopyIcon />
                    </ActionIconButton>
                  </Tooltip>
                  <Tooltip title="Download as CSV">
                    <ActionIconButton onClick={handleDownloadTableAsCSV}>
                      <DownloadIcon />
                    </ActionIconButton>
                  </Tooltip>
                </Flex>
              )}
            </ActionButtonsContainer>
          }
          defaultExpanded
        >
          {displayDescription && (
            <Description sx={{ mb: '16px' }}>{displayDescription}</Description>
          )}
          {data?.data && (
            <>
              {type === 'table' && (
                <SamplesTable
                  data={getTableData(data).data || []}
                  columns={getTableData(data).headers}
                />
              )}
              {type === 'schema_changes' && (
                <SchemaChangesList
                  displayName={getSchemaChangesData(data).displayName}
                  description={getSchemaChangesData(data).description}
                />
              )}
              {type === 'cloud_data_contract' &&
                getCloudDataContractData(data).changes.length > 0 && (
                  <CloudDataContractList
                    changes={getCloudDataContractData(data).changes}
                  />
                )}

              {type === 'anomaly_chart' && (
                <FlexColumn sx={{ position: 'relative' }}>
                  {isCloudTest && (
                    <Tooltip title="Exclude specific time range from the training period">
                      <WidgetButtonStyled onClick={handleExcludeRange}>
                        EXCLUDE
                      </WidgetButtonStyled>
                    </Tooltip>
                  )}

                  <AnomalyChart
                    metrics={getAnomalyChartData(data).metrics}
                    metricsTitle={getAnomalyChartData(data).metricsTitle}
                    failOnZero={getAnomalyChartData(data).failOnZero}
                    anomalyDirection={
                      getAnomalyChartData(data).anomalyDirection
                    }
                    detectionPeriod={getAnomalyChartData(data).detectionPeriod}
                    detectionDelay={getAnomalyChartData(data).detectionDelay}
                    status={data.status}
                    onFeedbackClick={
                      canGiveFeedback
                        ? (bucketStart) =>
                            setFeedbackDialog({
                              entryPoint: 'chart_tooltip',
                              bucketStart,
                            })
                        : undefined
                    }
                    changepointMarkers={changepoints.markers}
                  />
                </FlexColumn>
              )}

              {type === 'freshness_chart' && (
                <FreshnessChart
                  lastTimestamp={getFreshnessChartData(data).lastTimestamp}
                  status={data.status}
                  maxLoadedAtTimeAgoInSec={
                    getFreshnessChartData(data).maxLoadedAtTimeAgoInS
                  }
                  warnAfterRange={getFreshnessChartData(data).warnAfterRange}
                  errorAfterRange={getFreshnessChartData(data).errorAfterRange}
                  loadedAtField={getFreshnessChartData(data).loadedAtField}
                />
              )}
              {type === 'auto_freshness' && (
                <AutoFreshnessChart
                  metrics={getAutoFreshnessData(data).metrics}
                  status={data.status}
                />
              )}
            </>
          )}
        </Expandable>
      )}

      {data?.query && (
        <Expandable
          title="Query"
          defaultExpanded
          actionButtonTitle="Copy result query"
          actionButtonIcon={<CopyIcon width={16} />}
          onActionButtonClick={() => {
            navigator.clipboard.writeText(data.query);
            openSnackbar({ message: 'Copied to clipboard' });
          }}
        >
          <CodeEditor
            mode="sql"
            warehouseType={warehouseType}
            value={data.query}
            readOnly
          />
        </Expandable>
      )}

      {feedbackDialog && feedbackTest && feedbackResult && (
        <ResultFeedbackDialog
          onClose={() => setFeedbackDialog(undefined)}
          test={feedbackTest}
          result={feedbackResult}
          availableTypes={availableFeedbackTypes}
          entryPoint={feedbackDialog.entryPoint}
          defaultBucketStart={feedbackDialog.bucketStart}
          changepointMarkers={changepoints.pickerMarkers}
          existingChangepoints={changepoints.existingChangepoints}
        />
      )}

      {changepoints.changepointToRemove && (
        <RemoveChangepointDialog
          feedbackId={changepoints.changepointToRemove}
          onClose={changepoints.closeRemoveDialog}
          track={changepoints.track}
        />
      )}
    </>
  );
};

export default TestOverviewExecutionDetails;
